import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { X } from 'lucide-react'

// Zásobník otevřených modalů — Escape zavírá jen ten nejvrchnější (confirm nad formulářem apod.).
const stack: string[] = []
let scrollLocks = 0

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

const WIDTHS: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
}

/**
 * Modální okno v portálu do <body>.
 *
 * - Desktop/tablet (sm+): okno uprostřed. Mobil: spodní panel (sheet) přes celou šířku,
 *   s `mobileFullscreen` na celou obrazovku (dlouhé formuláře).
 * - Escape zavře (jen nejvrchnější modal). Obsah se posouvá, hlavička a patička zůstávají.
 * - Zavření klikem na pozadí JEN když stisk i puštění proběhly na pozadí — tah myší při
 *   označování textu, který skončí mimo okno, modal nezavře (povinný vzor, viz README).
 * - `dismissible={false}` — pozadí ani Escape nezavírají (rozpracovaný formulář s rizikem ztráty).
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  headerAction,
  size = 'md',
  mobileFullscreen = false,
  dismissible = true,
  bodyClassName,
  fixedHeight = false,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
  children: ReactNode
  /** Patička s akcemi; na mobilu tlačítka na plnou šířku pod sebou (primární nahoře). */
  footer?: ReactNode
  /** Akce v hlavičce vedle zavíracího křížku (např. „Uložit" na mobilu). */
  headerAction?: ReactNode
  size?: ModalSize
  mobileFullscreen?: boolean
  dismissible?: boolean
  bodyClassName?: string
  /** Okno nejvýš na 80 % výšky obrazovky i na desktopu: hlavička a patička stojí, posouvá se jen obsah (dlouhé seznamy). */
  fixedHeight?: boolean
}) {
  const id = useId()
  const titleId = `${id}-title`
  const downOnBackdrop = useRef(false)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    stack.push(id)
    scrollLocks++
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible && stack[stack.length - 1] === id) {
        e.stopPropagation()
        closeRef.current()
      }
    }
    document.addEventListener('keydown', onKey)
    // Fokus do okna (čtečky, klávesnice) — jen pokud uvnitř ještě nic fokus nemá (autoFocus pole).
    const t = setTimeout(() => {
      if (panelRef.current && !panelRef.current.contains(document.activeElement)) panelRef.current.focus()
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('keydown', onKey)
      const i = stack.lastIndexOf(id)
      if (i >= 0) stack.splice(i, 1)
      scrollLocks = Math.max(0, scrollLocks - 1)
      if (scrollLocks === 0) document.body.style.overflow = ''
    }
  }, [open, id, dismissible])

  if (!open) return null

  return createPortal(
    <div
      // data-overlay: okno otevřené z plovoucího panelu (filtr seznamu) — klik v něm panel nezavře.
      data-overlay
      className={clsx(
        'fixed inset-0 z-[90] flex animate-backdrop-in justify-center bg-slate-900/50 sm:items-start sm:overflow-y-auto sm:p-6 lg:p-10',
        mobileFullscreen ? 'items-stretch' : 'items-end',
      )}
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget }}
      onClick={(e) => {
        if (dismissible && downOnBackdrop.current && e.target === e.currentTarget) onClose()
        downOnBackdrop.current = false
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(
          // Okno dostává fokus programově (tabIndex=-1) — bez viditelného rámu, fokus ukazují jen ovládací prvky.
          'flex w-full flex-col bg-white shadow-soft-lg outline-none focus:outline-none focus-visible:outline-none ring-1 ring-slate-900/5',
          'animate-sheet-up sm:animate-fade-in sm:my-auto sm:max-h-[calc(var(--app-height)-3rem)] sm:rounded-2xl',
          mobileFullscreen ? 'h-[var(--app-height)] sm:h-auto' : fixedHeight ? 'max-h-[80vh] rounded-t-2xl sm:max-h-[80vh]' : 'max-h-[92vh] rounded-t-2xl sm:max-h-none',
          WIDTHS[size],
        )}
      >
        {/* Úchyt sheetu — jen vizuální nápověda na mobilu */}
        {!mobileFullscreen && <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-slate-300 sm:hidden" aria-hidden />}
        <header className={clsx('flex shrink-0 items-center gap-4 border-b border-slate-100 px-5 py-4 sm:px-6 sm:py-5', mobileFullscreen && 'pt-safe')}>
          {icon && <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">{icon}</span>}
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="truncate text-base font-semibold leading-snug text-ink-900 sm:text-lg">{title}</h2>
            {subtitle && <div className="mt-0.5 truncate text-sm text-ink-500">{subtitle}</div>}
          </div>
          {headerAction}
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="-mr-1.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-ink-500 hover:bg-slate-100 hover:text-ink-900"
          >
            <X size={20} />
          </button>
        </header>
        <div className={clsx('min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6', bodyClassName)}>{children}</div>
        {footer && (
          <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:gap-3 sm:rounded-b-2xl sm:px-6 sm:pb-4 [&>*]:w-full sm:[&>*]:w-auto">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}
